/**
 * useBillingAutoRefresh — WEB-936 п.5 (ретест QA 02.08, кросс-вкладочный сценарий).
 *
 * Проблема шире исходной карточки: после списания (генерация/чат) не
 * обновляется НИ шапка (HeaderBalanceWidget), НИ таблица истории операций
 * (TransactionsTable) — пока не перезагрузить страницу. Прошлым батчем уже
 * вливали рефетч истории «по смене balanceCents» (TransactionsTable), но он
 * не срабатывает именно кросс-вкладочно: Redux-стор существует ОТДЕЛЬНО в
 * каждой вкладке, и если операция потратившая деньги произошла в ДРУГОЙ
 * вкладке, то balanceCents в ЭТОЙ вкладке никто не меняет — dispatch
 * fetchBillingThunk в одной вкладке не долетает до Redux-стора другой.
 *
 * В проекте нет ни websocket, ни SSE-канала для billing (SSE есть только у
 * useFunnelGenerationStream/useEditThreadStream — это job-scoped стримы
 * конкретной генерации, не общий канал), поэтому закрыть именно чтение
 * баланса можно только опросом:
 *
 *  - интервал (по умолчанию 30 с — тот же порядок, что и heartbeat
 *    useAutosave.ts, уже принятый в проекте как «не агрессивно» для
 *    периодического запроса к бэкенду) — не тикает вовсе, пока вкладка скрыта
 *    (document.hidden), чтобы не жечь /billing в фоне;
 *  - + рефетч на возврат видимости (visibilitychange) и фокуса окна (focus) —
 *    дёшево (событийно, не по таймеру) и закрывает кросс-вкладочный сценарий
 *    МГНОВЕННО: как только пользователь переключается на вкладку «Баланс»,
 *    billing перезапрашивается, не дожидаясь целого интервала.
 *
 * Дальше per-tab: обновлённый balanceCents в Redux этой вкладки — тот же
 * сигнал, на который уже реагирует TransactionsTable (см. её WEB-936 п.5
 * watcher) — эта таблица подхватывает смену баланса и сама перезапрашивает
 * историю. Этот хук НИЧЕГО не знает о TransactionsTable — просто держит
 * billing свежим по времени/видимости, остальное уже работало.
 *
 * НЕ трогает списание/суммы/бэкенд — только момент, когда фронт перечитывает
 * уже существующий GET /billing.
 *
 * WEB-936 п.5 (третье подтверждение QA 08.08, зонт WEB-1118): интервал +
 * visibilitychange/focus ЗАКРЫВАЮТ «вернулся на вкладку», но НЕ закрывают
 * «вкладка/окно просто открыты рядом и их не покидали» — в такой раскладке
 * document.hidden может ни разу не стать true, а window не терять фокус, так
 * что событийный путь молчит, и остаётся ждать до 30с интервала. Добавлен
 * третий, независимый от видимости/фокуса канал: браузерное 'storage'-событие
 * (см. utils/billingSync.ts) — летит во ВСЕ прочие вкладки origin сразу при
 * записи, даже в скрытую вкладку. Слушатель НЕ гейтится document.hidden — это
 * весь смысл канала.
 *
 * WEB-1337 P6 (живой баг 15.08): две вкладки РАЗНЫХ организаций зациклили
 * обновление баланса через этот же storage-канал — см. полный разбор
 * пинг-понг-петли в utils/billingSync.ts doc-comment у `broadcastBalanceSync`.
 * Фикс: storage-слушатель ниже теперь сверяет organizationId, с которым был
 * широковещён баланс (utils/billingSync.ts readBroadcastOrgId), с
 * organizationId, активным В ЭТОЙ вкладке (state.organizations.activeOrgId).
 *
 * Ф1 (ревизия десятки 3, живой баг 15.08 — пара «личная вкладка ↔
 * орг-вкладка»): P6 закрывал только пару орг↔другая-орг — сравнение
 * `activeOrgIdRef.current && broadcastOrgId && ...` трактовало ЛИЧНЫЙ режим
 * (activeOrgId===null) как «неизвестно» и пропускало гейт, так что личная
 * вкладка рефетчила на КАЖДЫЙ чужой orgId-сигнал, сама писала свой (без
 * orgId) сигнал в ответ — цикл был жив именно для этой пары. Теперь личный
 * контекст сравнивается СТРОГО как PERSONAL_BROADCAST_SENTINEL (см.
 * utils/billingSync.ts): рефетч происходит, только когда broadcast-контекст
 * СОВПАДАЕТ со своим (оба orgId, оба personal), либо когда broadcastOrgId
 * вообще не задан (null — единственный настоящий «неизвестно», обратная
 * совместимость со старым клиентом до этого фикса).
 */
import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from './hooks';
import { fetchBillingThunk } from '@/store/billingSlice';
import {
  BILLING_SYNC_STORAGE_KEY,
  PERSONAL_BROADCAST_SENTINEL,
  readBroadcastOrgId,
} from '@/utils/billingSync';

/** 30 с — тот же heartbeat-порядок, что и useAutosave.ts; не «раз в секунду». */
export const BILLING_AUTO_REFRESH_INTERVAL_MS = 30000;

export function useBillingAutoRefresh(
  intervalMs: number = BILLING_AUTO_REFRESH_INTERVAL_MS,
): void {
  const dispatch = useAppDispatch();
  // WEB-1337 P6: активная организация ЭТОЙ вкладки — сверяется со
  // storage-broadcast'ом при 'storage'-событии (см. doc-comment выше).
  // Optional chaining: тестовые сторы без organizations-редьюсера (а также
  // самое раннее время жизни приложения до первой загрузки организаций)
  // читаются как undefined/null — обратная совместимость «рефетчить всегда».
  const activeOrgId = useAppSelector(
    (state) => (state as { organizations?: { activeOrgId: string | null } }).organizations
      ?.activeOrgId ?? null,
  );
  // Ref, чтобы обработчики не пересоздавались/не переустанавливали
  // таймер/слушатели при каждом ре-рендере — эффект ниже с пустым (кроме
  // intervalMs) списком зависимостей ставит их один раз за маунт.
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const activeOrgIdRef = useRef(activeOrgId);
  activeOrgIdRef.current = activeOrgId;

  useEffect(() => {
    const refresh = () => {
      if (document.hidden) return;
      dispatchRef.current(fetchBillingThunk());
    };

    const interval = setInterval(refresh, intervalMs);

    // Возврат на вкладку — это ДВА события подряд (visibilitychange + focus).
    // Без схлопывания каждое переключение вкладки давало бы два одинаковых
    // GET /billing; ручка денежная, дублировать её на ровном месте не надо.
    // Схлопываем в микротаске: оба события одного возврата приходят в одном
    // тике, следующий возврат (и тик интервала) уже не затронут.
    let eventRefreshQueued = false;
    const refreshOnce = () => {
      if (document.hidden || eventRefreshQueued) return;
      eventRefreshQueued = true;
      queueMicrotask(() => {
        eventRefreshQueued = false;
      });
      refresh();
    };

    document.addEventListener('visibilitychange', refreshOnce);
    window.addEventListener('focus', refreshOnce);

    // WEB-936 п.5: кросс-вкладочный сигнал — НЕ гейтится document.hidden
    // (в отличие от refresh() выше), потому что должен пробить именно
    // скрытую/невидимую-фактически-но-не-по-API вкладку.
    const onStorage = (event: StorageEvent) => {
      if (event.key !== BILLING_SYNC_STORAGE_KEY) return;
      // WEB-1337 P6 + Ф1 (ревизия 15.08): игнорируем сигнал ЯВНО чужого
      // контекста — иначе вкладка B рефетчит СВОЙ billing по чужому сигналу,
      // пишет СВОЙ (другой) баланс обратно в тот же ключ, и это ретриггерит
      // вкладку A — бесконечный пинг-понг. Раньше личная вкладка
      // (activeOrgIdRef.current===null) трактовалась как «свой контекст
      // неизвестен» и потому рефетчила ВСЕГДА — цикл был жив ровно для пары
      // «личная ↔ орг». Теперь личный режим — такой же явный, различимый
      // идентификатор (PERSONAL_BROADCAST_SENTINEL), как и любой orgId:
      // сравниваем СТРОГО ownContext vs broadcastOrgId. Единственный случай
      // безусловного рефетча — когда broadcastOrgId вообще ещё не писался
      // (null, старый клиент до этого фикса, обратная совместимость).
      const ownContext = activeOrgIdRef.current ?? PERSONAL_BROADCAST_SENTINEL;
      const broadcastOrgId = readBroadcastOrgId();
      if (broadcastOrgId !== null && broadcastOrgId !== ownContext) {
        return;
      }
      dispatchRef.current(fetchBillingThunk());
    };
    window.addEventListener('storage', onStorage);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshOnce);
      window.removeEventListener('focus', refreshOnce);
      window.removeEventListener('storage', onStorage);
    };
  }, [intervalMs]);
}
